import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Container } from 'semantic-ui-react';
import { searchContent } from '@plone/volto/actions/search/search';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import FlexPublicationCard from '../../Listing/Publication/FlexPublicationCard';
import Sections from '../Helpers/Sections';
import DefaultViewWithoutTitle from '../../Custom/DefaultViewWithoutTitle';
import { flattenRichTextToAppURL } from '../../../helpers/richText';

const VIDEO_EXT = /\.(mp4|webm|ogv|mov)$/i;
const EMPTY = [];

// The item's Lead Image as the player's poster frame: without one the player
// is a black rectangle until the browser has pulled enough of the file to
// decode a frame - which on the larger videos here means it stays black for
// a while. The poster shows straight away and the video starts on play.
// 'large' (800px) is the smallest scale wider than the player's 640px cap.
const posterUrl = (image) => {
  if (!image) return undefined;
  const scale =
    image.scales?.large?.download ||
    image.scales?.preview?.download ||
    image.download;
  return scale ? flattenToAppURL(scale) : undefined;
};

// Turns "BMVC_2024_Villar_Supplementary" into "BMVC 2024 Villar Supplementary" -
// the raw filename-derived title is all these child File objects usually have.
const humanizeLabel = (title, id) => {
  const raw = (title || id || '').replace(VIDEO_EXT, '');
  return raw.replace(/[_-]+/g, ' ').trim();
};

const VideoView = (props) => {
  const { content } = props;

  const videoFiles = (content.items || []).filter((item) =>
    VIDEO_EXT.test(item.id || item['@id'] || ''),
  );
  const hasVideos = videoFiles.length > 0;
  const showLabels = videoFiles.length > 1;

  const hasResources =
    content.video_resources?.data && content.video_resources.data !== '<p></p>';

  // The publication records its videos, so the video looks itself up there.
  const dispatch = useDispatch();
  const subrequest = `${content['@id']}-publications`;
  useEffect(() => {
    if (content.UID) {
      dispatch(
        searchContent(
          '',
          {
            portal_type: 'publication',
            related_videos: content.UID,
            metadata_fields: '_all',
          },
          subrequest,
        ),
      );
    }
  }, [dispatch, content.UID, subrequest]);
  const publications = useSelector(
    (state) => state.search.subrequests?.[subrequest]?.items ?? EMPTY,
  );
  const showPublication = publications.length > 0;

  return (
    <Container id="view-wrapper" className="VideoView">
      {/* Header */}
      <div className="view-header">
        <h2 className="view-header__title">{content.title || content.id}</h2>
        {/* No description here. `description` is a listing field - the one
            line that goes on a card and into a search result - and repeating
            it as the first paragraph of the page it summarises says the same
            thing twice. The full view has its own content. */}
        <div className="view-header__divider" />
      </div>
      <Sections>
        {/* The publication as its full citation card (authors, venue, links),
            like the "Video Attachment for: <authors>: <title>" line the old
            video pages opened with. */}
        {showPublication && (
          <div className="view-section">
            <h3 className="view-section__label">Video Attachment for:</h3>
            <div className="cards__container">
              {publications.map((item) => (
                <FlexPublicationCard key={item['@id']} item={item} />
              ))}
            </div>
          </div>
        )}

        {/* Video Player(s) */}
        {hasVideos && (
          <>
            <div className="view-section view-section--videos">
              {videoFiles.map((item) => (
                <div className="view-section__video" key={item['@id']}>
                  {showLabels && (
                    <h3 className="view-section__label">
                      {humanizeLabel(item.title, item.id)}
                    </h3>
                  )}
                  {/* eslint-disable-next-line jsx-a11y/media-has-caption --
                      these are supplementary paper videos with no narration
                      and no caption track to point at; an empty <track> would
                      claim captions that do not exist. */}
                  <video
                    controls
                    preload="metadata"
                    poster={posterUrl(content.image)}
                    className="view-section__video-player"
                    src={`${flattenToAppURL(item['@id'])}/@@download/file`}
                  >
                    Your browser does not support embedded video playback.
                  </video>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Resources */}
        {hasResources && (
          <>
            <div className="view-section">
              <h3 className="view-section__label">Resources:</h3>
              <div
                className="view-section__richtext"
                dangerouslySetInnerHTML={{
                  __html: flattenRichTextToAppURL(content.video_resources.data),
                }}
              />
            </div>
          </>
        )}
      </Sections>

      {/* The block-rendered part of the page */}
      <DefaultViewWithoutTitle {...props} />
    </Container>
  );
};

VideoView.propTypes = {
  content: PropTypes.shape({
    title: PropTypes.string,
    id: PropTypes.string,
    description: PropTypes.string,
    image: PropTypes.shape({
      download: PropTypes.string,
      scales: PropTypes.object,
    }),
    video_resources: PropTypes.shape({ data: PropTypes.string }),
    '@id': PropTypes.string,
    UID: PropTypes.string,
    items: PropTypes.arrayOf(
      PropTypes.shape({
        '@id': PropTypes.string,
        id: PropTypes.string,
        title: PropTypes.string,
      }),
    ),
  }).isRequired,
};

export default VideoView;
